import { t, tArray } from '../i18n';
import { SignupFormData, SignupResponse } from '../types';
import { post } from '../utils/api';
import { validateEmail, validatePhone, validateRequired, validateUrl } from '../utils/validators';

export function renderLandingPage(
  onSignupSuccess: (customId?: string) => void
): HTMLElement {
  const page = document.createElement('div');
  page.className = 'min-h-screen relative';

  const partner = new URLSearchParams(window.location.search).get('partner') || '';
  const features = tArray('hero.features');

  const bgImageUrl = 'https://onboarding.openhandwerk.de/img/bg-oh.jpg';
  const logoUrl = '/logo-oh-white.png';

  page.innerHTML = `
    <!-- Background with overlay -->
    <div class="fixed inset-0 z-0">
      <div class="absolute inset-0 bg-brand-800" style="background-image: url('${bgImageUrl}'); background-size: cover; background-position: top left;"></div>
      <div class="absolute inset-0 bg-gradient-to-br from-brand-900/90 via-brand-800/85 to-brand-700/80"></div>
      <div class="absolute inset-0 opacity-[0.03]" style="background-image: url(&quot;data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E&quot;);"></div>
    </div>

    <!-- Content -->
    <div class="relative z-10 min-h-screen flex flex-col">
      <div class="flex-1 flex items-center">
        <div class="mx-auto w-full max-w-7xl px-4 py-8 lg:px-8 lg:py-12">
          <div class="grid items-center gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-24">

            <!-- Left: Hero -->
            <div class="text-white animate-fade-in-up">
              <img src="${logoUrl}" alt="openHandwerk" class="h-16 mb-8 animate-fade-in drop-shadow-lg" onerror="this.style.display='none'">

              <div class="mb-6 animate-fade-in-up">
                <span class="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-1.5 text-sm font-semibold text-emerald-300 ring-1 ring-emerald-400/25 backdrop-blur-sm">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  ${t('hero.badge')}
                </span>
              </div>

              <h1 class="font-display text-4xl font-extrabold leading-[1.1] tracking-tight lg:text-5xl xl:text-[3.5rem]">
                ${t('hero.title')}
              </h1>

              <p class="mt-2 font-display text-xl font-bold tracking-wide text-silver uppercase lg:text-2xl animate-fade-in-up stagger-1">
                ${t('hero.titleHighlight')}
              </p>

              <p class="mt-6 text-lg leading-relaxed text-white/60 max-w-lg animate-fade-in-up stagger-2">
                ${t('hero.subtitle')}
              </p>

              <ul class="mt-8 space-y-3 animate-fade-in-up stagger-3">
                ${features.map((f, i) => `
                  <li class="flex items-center gap-3 text-white/80 stagger-${i + 1}" style="animation-delay: ${0.3 + i * 0.1}s;">
                    <div class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-silver/20 ring-1 ring-silver/30">
                      <svg class="h-3.5 w-3.5 text-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                    <span class="text-[15px]">${f}</span>
                  </li>
                `).join('')}
              </ul>

              <div class="mt-10 flex items-center gap-8 text-sm text-white/40 animate-fade-in-up stagger-5">
                <div class="flex items-center gap-2">
                  <svg class="h-5 w-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <span>Keine Kreditkarte</span>
                </div>
                <div class="flex items-center gap-2">
                  <svg class="h-5 w-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <span>DSGVO-konform</span>
                </div>
                <div class="flex items-center gap-2">
                  <svg class="h-5 w-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <span>SSL-verschlüsselt</span>
                </div>
              </div>
            </div>

            <!-- Right: Signup form -->
            <div class="animate-fade-in-up stagger-2">
              <div class="rounded-2xl bg-white shadow-elevated p-8 lg:p-10 relative overflow-hidden">
                <!-- Top accent bar -->
                <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-400 via-brand-500 to-brand-600"></div>

                <div class="mb-6">
                  <p class="text-xs font-bold uppercase tracking-[0.2em] text-silver-dark">${t('form.subtitle')}</p>
                  <h2 class="mt-2 font-display text-xl font-bold text-surface-800">${t('form.title')}</h2>
                </div>

                <div id="form-error" class="mb-4 hidden rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <div class="flex items-center gap-2">
                    <svg class="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span id="form-error-text"></span>
                  </div>
                </div>

                <form id="signup-form" class="space-y-4">
                  <!-- Salutation -->
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2 cursor-pointer group">
                      <input type="radio" name="salutation" value="Herr" checked
                        class="h-4 w-4 border-surface-300 text-brand-500 focus:ring-brand-500">
                      <span class="text-sm text-surface-600 group-hover:text-surface-800 transition-colors">${t('form.herr')}</span>
                    </label>
                    <label class="flex items-center gap-2 cursor-pointer group">
                      <input type="radio" name="salutation" value="Frau"
                        class="h-4 w-4 border-surface-300 text-brand-500 focus:ring-brand-500">
                      <span class="text-sm text-surface-600 group-hover:text-surface-800 transition-colors">${t('form.frau')}</span>
                    </label>
                  </div>

                  <!-- Name row -->
                  <div class="grid gap-3 sm:grid-cols-2">
                    <div>
                      <input type="text" id="firstname" placeholder="${t('form.firstname')} *" required class="oh-input-card">
                      <p class="mt-1 hidden text-xs text-red-500" id="err-firstname">${t('form.errors.firstname')}</p>
                    </div>
                    <div>
                      <input type="text" id="lastname" placeholder="${t('form.lastname')} *" required class="oh-input-card">
                      <p class="mt-1 hidden text-xs text-red-500" id="err-lastname">${t('form.errors.lastname')}</p>
                    </div>
                  </div>

                  <div>
                    <input type="email" id="email" placeholder="${t('form.email')} *" required class="oh-input-card">
                    <p class="mt-1 hidden text-xs text-red-500" id="err-email">${t('form.errors.email')}</p>
                  </div>

                  <div>
                    <input type="text" id="company" placeholder="${t('form.company')} *" required class="oh-input-card">
                    <p class="mt-1 hidden text-xs text-red-500" id="err-company">${t('form.errors.company')}</p>
                  </div>

                  <div class="grid gap-3 sm:grid-cols-2">
                    <div>
                      <input type="tel" id="phone" placeholder="${t('form.phone')} *" required class="oh-input-card">
                      <p class="mt-1 hidden text-xs text-red-500" id="err-phone">${t('form.errors.phone')}</p>
                    </div>
                    <div>
                      <input type="url" id="website" placeholder="${t('form.website')}" class="oh-input-card">
                      <p class="mt-1 hidden text-xs text-red-500" id="err-website">${t('form.errors.website')}</p>
                    </div>
                  </div>

                  <!-- Checkboxes -->
                  <div class="space-y-3 pt-2">
                    <label class="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" id="newsletter"
                        class="mt-0.5 h-4 w-4 rounded border-surface-300 text-brand-500 focus:ring-brand-500">
                      <span class="text-sm text-surface-500 group-hover:text-surface-700 transition-colors">${t('form.newsletter')}</span>
                    </label>
                    <label class="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" id="agb" required
                        class="mt-0.5 h-4 w-4 rounded border-surface-300 text-brand-500 focus:ring-brand-500">
                      <span class="text-sm text-surface-500 group-hover:text-surface-700 transition-colors">${t('form.agb')}</span>
                    </label>
                    <p class="hidden text-xs text-red-500" id="err-agb">${t('form.errors.agb')}</p>
                  </div>

                  <button type="submit" id="submit-btn" class="oh-btn-cta w-full gap-2">
                    ${t('form.submit')}
                    <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                    </svg>
                  </button>
                </form>

                <!-- SSL footer -->
                <div class="mt-5 flex items-center justify-center gap-2 text-xs text-surface-400">
                  <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                  <span>256-bit SSL Verschlüsselung</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="relative z-10 border-t border-white/10 py-5 text-center text-sm text-white/30">
        <div class="mx-auto max-w-7xl px-4 flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <p>&copy; ${new Date().getFullYear()} ${t('footer.copyright')}</p>
          <div class="flex gap-6">
            <a href="https://openhandwerk.de/datenschutzerklaerung" class="hover:text-white/60 transition-colors" target="_blank">${t('footer.privacy')}</a>
            <a href="https://openhandwerk.de/impressum" class="hover:text-white/60 transition-colors" target="_blank">${t('footer.imprint')}</a>
            <a href="https://openhandwerk.de/agb" class="hover:text-white/60 transition-colors" target="_blank">AGB</a>
          </div>
        </div>
      </footer>
    </div>
  `;

  const form = page.querySelector('#signup-form') as HTMLFormElement;
  const errorEl = page.querySelector('#form-error') as HTMLElement;
  const errorText = page.querySelector('#form-error-text') as HTMLElement;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const firstname = (page.querySelector('#firstname') as HTMLInputElement).value;
    const lastname = (page.querySelector('#lastname') as HTMLInputElement).value;
    const email = (page.querySelector('#email') as HTMLInputElement).value;
    const company = (page.querySelector('#company') as HTMLInputElement).value;
    const phone = (page.querySelector('#phone') as HTMLInputElement).value;
    const website = (page.querySelector('#website') as HTMLInputElement).value;
    const agb = (page.querySelector('#agb') as HTMLInputElement).checked;

    errorEl.classList.add('hidden');
    let hasError = false;

    function showErr(id: string, show: boolean) {
      page.querySelector(`#err-${id}`)?.classList.toggle('hidden', !show);
      if (show) hasError = true;
    }

    showErr('firstname', !validateRequired(firstname));
    showErr('lastname', !validateRequired(lastname));
    showErr('email', !validateEmail(email));
    showErr('company', !validateRequired(company));
    showErr('phone', !validatePhone(phone));
    showErr('website', !validateUrl(website));
    showErr('agb', !agb);

    if (hasError) {
      errorText.textContent = t('form.errors.general');
      errorEl.classList.remove('hidden');
      page.querySelector('.text-red-500:not(.hidden)')?.previousElementSibling?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    const submitBtn = page.querySelector('#submit-btn') as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `
      <svg class="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
      </svg>
      ${t('form.submitting')}
    `;

    const formData: SignupFormData = {
      salutation: (page.querySelector('input[name="salutation"]:checked') as HTMLInputElement).value,
      firstname: firstname.trim(),
      lastname: lastname.trim(),
      email: email.trim(),
      phone: phone.trim(),
      website: website.trim(),
      company: company.trim(),
      newsletter: (page.querySelector('#newsletter') as HTMLInputElement).checked,
      partner,
    };

    try {
      const response = await post<SignupResponse>('/signup', formData);
      if (response.success && response.data) {
        onSignupSuccess(response.data.customId);
      } else {
        if (response.field_errors) {
          Object.entries(response.field_errors).forEach(([field, message]) => {
            const fieldError = page.querySelector(`#err-${field}`) as HTMLElement | null;
            if (fieldError) {
              fieldError.textContent = message;
              fieldError.classList.remove('hidden');
            }
          });
        }
        errorEl.classList.remove('hidden');
        errorText.textContent = [response.error, response.message].filter(Boolean).join(' — ') || t('form.errors.failed');
      }
    } catch {
      errorEl.classList.remove('hidden');
      errorText.textContent = t('form.errors.failed');
    } finally {
      submitBtn.disabled = false;
      submitBtn.innerHTML = `
        ${t('form.submit')}
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
        </svg>
      `;
    }
  });

  return page;
}
